import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { DocumentMaster } from "../../../services/documentMasterService";
import Button from "../../../shared/components/ui/button/Button";

const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

interface Props {
  onAdd: (documentData: {
    documentName: string;
    documentDescription?: string;
    status?: 'Active' | 'Inactive';
  }) => Promise<void>;
  onClose?: () => void;
  editingDocument?: DocumentMaster | null;
}

const schema = yup.object({
  documentName: yup.string()
    .required("Document name is required")
    .min(2, "Document name must be at least 2 characters")
    .max(200, "Document name must be less than 200 characters"),
  documentDescription: yup.string()
    .max(500, "Description must be less than 500 characters"),
});

interface FormValues {
  documentName: string;
  documentDescription: string;
}

export default function DocumentForm({ onAdd, onClose, editingDocument }: Props) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const isEditMode = Boolean(editingDocument);

  const getDefaultValues = () => {
    if (editingDocument) {
      return {
        documentName: editingDocument.documentName || '',
        documentDescription: editingDocument.documentDescription || '',
      };
    }
    return {
      documentName: '',
      documentDescription: '',
    };
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: getDefaultValues()
  });

  const watchedDocumentName = watch("documentName");
  const watchedDocumentDescription = watch("documentDescription");

  useEffect(() => {
    reset(getDefaultValues());
  }, [editingDocument, reset]);

  const onSubmit = async (data: FormValues) => {
    setApiError(null);
    setShowSuccess(false);

    try {
      const documentData = {
        documentName: data.documentName.trim(),
        documentDescription: data.documentDescription?.trim() || undefined,
        status: editingDocument?.status || 'Active' as 'Active' | 'Inactive',
      };

      await onAdd(documentData);

      setShowSuccess(true);
      toast.success(
        isEditMode
          ? "Document updated successfully!"
          : "Document created successfully!"
      );

      setTimeout(() => {
        onClose?.();
        reset();
      }, 1500);
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      let message = error.response?.data?.message || error.message || "Failed to save document. Please try again.";

      if (message.includes("duplicate key error") && message.includes("documentName")) {
        message = "Document name already exists. Please use a different name.";
      } else if (message.includes("Document validation failed")) {
        message = "Please check all required fields and try again.";
      }

      setApiError(message);
      console.error("Document Save Error:", error);
    }
  };

  const formTitle = isEditMode ? "Edit Document" : "Add New Document";

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col"
    >
      {/* Success Message */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.successLight,
              borderColor: `${BRAND_COLORS.success}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.success + '20',
                borderColor: BRAND_COLORS.success + '40'
              }}
            >
              <CheckCircle2 className="w-5 h-5" style={{ color: BRAND_COLORS.success }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.success }}>
                Document {isEditMode ? 'updated' : 'created'} successfully!
              </p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.success }}>
                {isEditMode ? 'Document information has been updated.' : 'Document has been added to the system.'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Alert */}
      <AnimatePresence>
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.errorLight,
              borderColor: `${BRAND_COLORS.error}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.error + '20',
                borderColor: BRAND_COLORS.error + '40'
              }}
            >
              <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.error }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.error }}>Error</p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.error }}>{apiError}</p>
            </div>
            <button
              onClick={() => setApiError(null)}
              className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
              style={{ color: BRAND_COLORS.error }}
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Header */}
      <div className="flex items-center gap-3 pb-5 border-b border-gray-200 mb-6">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center border"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
            borderColor: BRAND_COLORS.primary
          }}
        >
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">{formTitle}</h2>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            {isEditMode
              ? "Update document information"
              : "Create a new document"
            }
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      {/* Form Body - Scrollable */}
      <div className="flex-1 overflow-y-auto pr-2">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Document Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
              <h3 className="text-base font-semibold text-gray-900">Document Information</h3>
            </div>

            {/* Document Name */}
            <div className="space-y-2">
              <label htmlFor="documentName" className="block text-sm font-medium text-gray-900">
                Document Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText size={16} className="text-gray-500" />
                </div>
                <input
                  id="documentName"
                  type="text"
                  {...register("documentName")}
                  className={`h-10 w-full rounded-lg border bg-white pl-9 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                    errors.documentName
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : watchedDocumentName && !errors.documentName
                        ? `focus:ring-opacity-20`
                        : "border-gray-300 hover:border-gray-400"
                  }`}
                  style={watchedDocumentName && !errors.documentName ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.documentName ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter document name"
                />
                {watchedDocumentName && !errors.documentName && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.documentName && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.documentName && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.documentName.message}
                </p>
              )}
            </div>

            {/* Document Description */}
            <div className="space-y-2">
              <label htmlFor="documentDescription" className="block text-sm font-medium text-gray-900">
                Description
              </label>
              <div className="relative">
                <textarea
                  id="documentDescription"
                  {...register("documentDescription")}
                  rows={4}
                  className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                    errors.documentDescription
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : watchedDocumentDescription && !errors.documentDescription
                        ? `focus:ring-opacity-20`
                        : "border-gray-300 hover:border-gray-400"
                  }`}
                  style={watchedDocumentDescription && !errors.documentDescription ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.documentDescription ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter document description (optional)"
                />
              </div>
              {errors.documentDescription && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.documentDescription.message}
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !isValid}
              className="min-w-[120px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? "Updating..." : "Creating..."}
                </>
              ) : (
                isEditMode ? "Update Document" : "Create Document"
              )}
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
